import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Popup, Modal, Icon} from 'semantic-grab-ui/src';
import { Progress } from '@duxton-web/ui';
import { DownloadUniversal } from '@duxton-web/icons';
import _ from 'lodash';
import moment from 'moment';

import i18n from 'i18n-js';
import { getTimeProgress } from 'actions/newTridentExtra';
import TwoCellRow from 'components/custom/TwoCellRow';
import './WidgetComponent.scss';
import { getProgressPercent } from 'omega-rtc/utils/new-trident-utils';
import { Campaign } from 'types/ts/campaigns';
import { NodeWarning } from 'types/trident-campaigns';
import LimitShow from './LimitShow';
import SubActionMetaForm from './SubActionMetaForm';
import WarningList from './WarningList';
import LimitHitWarning from 'components/form/trident-campaigns/LimitHitWarning';

import { DATE_FORMAT } from './constraints';

interface Props {
  model: Campaign;
  currentNode: string;
  parentNode: string;
  currentNodeProgress: Object;
  getTimeProgress: Function;
  hideUsage: boolean;
  showSubActionDetail: boolean;
  warnings?: NodeWarning;
  listRuleTypes: Object;
  parentKeyList: Array<string>;
}

const getEventTypeData = (
  model: Campaign,
  listRuleTypes: Object,
  parentKeyList: Array<string>,
) => {
  const targetUserType = _.get(model, 'targetUserType');
  const treatmentStartNode = _.get(model, `nodes.${(parentKeyList || [])[0]}`);
  const eventTypes = _.map(
    _.get(treatmentStartNode, 'data.scenarios'),
    (scenario: { eventType: any }) => scenario.eventType,
  );
  let eventTypeData = {};
  eventTypes.forEach((eventType: any) => {
    const data = _.find(_.get(listRuleTypes, targetUserType, []), {
      eventType,
    });
    if (data) {
      eventTypeData = data;
    }
  });
  return eventTypeData;
};

export const ActionShow: React.FC<Props> = ({
  model,
  currentNode,
  parentNode,
  hideUsage,
  showSubActionDetail,
  warnings,
  listRuleTypes,
  parentKeyList,
}) => {
  const dispatch = useDispatch();
  const currentNodeProgressKey = `${_.get(model, 'countryID')}${_.get(model, 'campaignID')}${currentNode}`;
  const currentNodeProgress = useSelector((state) =>
    _.get(state, `newTridents.progress.${currentNodeProgressKey}`, {}),
  );

  React.useEffect(() => {
    const isDateTimeEvent =
      _.get(model, `nodes[${parentNode}].type`) === 'time';
    const lastHappenedAt = _.get(
      model,
      `nodes[${parentNode}].data.lastHappenedAt`,
      null,
    );

    if (isDateTimeEvent && lastHappenedAt !== null && lastHappenedAt !== '') {
      const params = {
        countryID: _.get(model, 'countryID'),
        campaignID: _.get(model, 'campaignID'),
        nodeID: currentNode,
        triggerTime: lastHappenedAt.format(),
      };
      dispatch(getTimeProgress(params));
    }
  }, [model, parentNode, currentNode, dispatch]);

  const getSubActionView = React.useCallback(
    (subAction: object, index: number) => {
      const actionType = _.get(subAction, 'type', '');
      const warnings = _.get(warnings, `data["${index}"]`, []);
      const eventTypeData = getEventTypeData(
        model,
        listRuleTypes,
        parentKeyList,
      );

      return (
        <div>
          <p>{actionType}</p>
          <WarningList warnings={warnings} />
          {showSubActionDetail && (
            <Modal trigger={<button>Details</button>} closeIcon>
              <Modal.Content>
                <SubActionMetaForm
                  model={model}
                  actionType={actionType}
                  eventType={_.get(eventTypeData, 'eventType', '')}
                  readonly
                  currentNode={currentNode}
                  subActionIndex={index}
                  onModelChange={() => {}}
                />
              </Modal.Content>
            </Modal>
          )}
        </div>
      );
    },
    [
      model,
      currentNode,
      showSubActionDetail,
      warnings,
      listRuleTypes,
      parentKeyList,
    ],
  );

  const isDateTimeEvent = _.get(model, `nodes[${parentNode}].type`) === 'time';
  const processedCount = _.get(currentNodeProgress, 'processedCount', 0);
  const totalCount = _.get(currentNodeProgress, 'totalCount', 0);
  const reportPath = _.get(currentNodeProgress, 'reportPath', '');
  const progressPercent = getProgressPercent(processedCount, totalCount);
  const limitWarnings = _.get(warnings, 'limits', []);
  const randomizeRange = _.get(
    model,
    `nodes[${currentNode}].randomizeRange`,
    [],
  );
  const totalUsage = _.get(
    model,
    `usage.actionUsages[${currentNode}].total`,
    0,
  );
  const lastUsage =
    totalUsage > 0
      ? moment(
        _.get(model, `usage.actionUsages[${currentNode}].lastUsage`),
      ).format(DATE_FORMAT)
      : undefined;
  const hideUsageByNodeType =
    _.get(model, `nodes[${parentKeyList[0]}].type`) === 'schedule';

  const campaignStartTime = _.get(model, 'startTime')
  const usageTrackingCutoffDate = moment('2025-02-13T00:00:00Z'); // Cutoff date when usage tracking table has been shifted to DDB
  const shouldDisplayUsageWarning = campaignStartTime && campaignStartTime.isBefore(usageTrackingCutoffDate);

  return (
    <div>
      <Table striped>
        <Table.Body>
          {!hideUsage && !hideUsageByNodeType && (
            <React.Fragment>
              <TwoCellRow
                firstCell="Usage today"
                secondCell={`${_.get(model, `usage.actionUsages[${currentNode}].daily`, 0)}`}
              />
              <TwoCellRow
                firstCell="Usage total"
                secondCell={
                  <div style={{ display: 'flex' }}>
                    {totalUsage}
                    {shouldDisplayUsageWarning &&
                        <span style={{marginLeft: 5}}>
                          <Popup trigger={<Icon name="info circle"/>}
                               content="This number may be lower than the actual figures because the usage count data for all campaigns before February 13 2025 has been reset. For accurate figures, please reach out to Trident Oncall via #trident-questions."
                               size="mini"
                          />
                        </span>
                    }
                    <div style={{ marginLeft: '10px' }}>
                      <LimitHitWarning
                        content={'Total limit hit'}
                        usage={_.get(
                          model,
                          `usage.actionUsages.${currentNode}.total`,
                        )}
                        limit={_.result(
                          _.find(
                            _.get(model, `nodes.${currentNode}.limits`),
                            (obj) => obj.per === 'total',
                          ),
                          'limit',
                        )}
                      />
                    </div>
                  </div>
                }
              />
              {!_.isEmpty(lastUsage) && (
                <TwoCellRow firstCell="Last Usage" secondCell={lastUsage} />
              )}
            </React.Fragment>
          )}
          {randomizeRange && randomizeRange.length > 0 && (
            <TwoCellRow
              firstCell="Random Percentage"
              secondCell={randomizeRange[1] - randomizeRange[0]}
            />
          )}
          <TwoCellRow
            firstCell="Limits"
            secondCell={
              <React.Fragment>
                <LimitShow
                  limits={_.get(model.nodes[currentNode], 'limits', [])}
                />
                <WarningList warnings={limitWarnings} />
              </React.Fragment>
            }
          />
          {_.get(model.nodes[currentNode], 'data', []).map(
            (subAction, index) => (
              <TwoCellRow
                key={index}
                firstCell={`Sub-action ${index + 1}`}
                secondCell={getSubActionView(subAction, index)}
              />
            ),
          )}

          {isDateTimeEvent && reportPath && (
            <TwoCellRow
              firstCell={i18n.t('common.download_report')}
              secondCell={
                <a href={reportPath} download>
                  {' '}
                  <DownloadUniversal />
                </a>
              }
            />
          )}
          {isDateTimeEvent && totalCount !== -1 && (
            <TwoCellRow
              firstCell={i18n.t('targets_page.fields.progress')}
              secondCell={
                <Popup
                  trigger={<Progress percent={progressPercent} progress />}
                  content={`Processed Count: ${processedCount}  Total Count: ${totalCount}`}
                />
              }
            />
          )}
        </Table.Body>
      </Table>
    </div>
  );
};

export default ActionShow;
